import { FC, SyntheticEvent, useState, useEffect, useRef } from 'react';
import { RawMaterial } from 'src/models/Management.interface';
import { formatCurrency } from 'src/utils/format.utils';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MenuItem, SelectChangeEvent } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import {
  fetchRawMaterialLogs,
  RawMaterialLog,
} from 'src/services/material.service';
import { RawMaterialLogTypeLabels } from 'src/models/enums/RawMaterialLogType.enum';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import StyledGridContainer, {
  RightAlignedCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import {
  StyledDialog,
  FixedHeaderWrapper,
  ContentWrapper,
  HeaderSection,
  HeaderLeft,
  TitleTypography,
  CloseIcon,
  StyledTabs,
  StyledTab,
  TabPanelContainer,
  DetailsTable,
  DetailsRow,
  DetailsCell,
  LogsFilterContainer,
  FilterField,
  FilterLabel,
  LogTypeSelect,
  FilterButtonsContainer,
  ApplyButton,
  ClearButton,
  LogTypeBadge,
  QuantityChange,
  DateFilterErrorText,
  ReferenceCellContainer,
} from './ViewRawMaterialDialog.styles';
import Status from 'src/components/Status';
import {
  StyledDatePickerTextField,
  StyledDatePickerPaper,
  CalendarIcon,
} from 'src/components/StyledComponents/StyledDatePicker';

interface ViewRawMaterialDialogProps {
  open: boolean;
  onClose: () => void;
  data: RawMaterial | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <TabPanelContainer
      role="tabpanel"
      hidden={value !== index}
      id={`raw-material-tabpanel-${index}`}
      aria-labelledby={`raw-material-tab-${index}`}
    >
      {value === index && children}
    </TabPanelContainer>
  );
};

// Get default dates
const getFirstDayOfMonth = (): Dayjs => {
  return dayjs().startOf('month');
};

const getCurrentDate = (): Dayjs => {
  return dayjs();
};

const ViewRawMaterialDialog: FC<ViewRawMaterialDialogProps> = ({
  open,
  onClose,
  data,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const [tabValue, setTabValue] = useState(0);

  // Logs filter state
  const [startDate, setStartDate] = useState<Dayjs | null>(
    getFirstDayOfMonth()
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(getCurrentDate());
  const [logType, setLogType] = useState<string>('all');

  // Date validation errors
  const [dateErrors, setDateErrors] = useState<{
    startDate: string;
    endDate: string;
  }>({ startDate: '', endDate: '' });

  // Applied filter state (used for API calls)
  const [appliedStartDate, setAppliedStartDate] = useState<Dayjs | null>(
    getFirstDayOfMonth()
  );
  const [appliedEndDate, setAppliedEndDate] = useState<Dayjs | null>(
    getCurrentDate()
  );
  const [appliedLogType, setAppliedLogType] = useState<string>('all');

  // Logs data state
  const [logs, setLogs] = useState<RawMaterialLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt', sort: 'desc' },
  ]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleClose = () => {
    setTabValue(0);
    // Reset filters to defaults
    setStartDate(getFirstDayOfMonth());
    setEndDate(getCurrentDate());
    setLogType('all');
    setAppliedStartDate(getFirstDayOfMonth());
    setAppliedEndDate(getCurrentDate());
    setAppliedLogType('all');
    setLogs([]);
    setPage(1);
    setSortBy('createdAt');
    setOrder('DESC');
    setSortModel([{ field: 'createdAt', sort: 'desc' }]);
    setDateErrors({ startDate: '', endDate: '' });
    onClose();
  };

  const fetchLogs = async () => {
    if (!data?.id) return;

    setLogsLoading(true);
    try {
      const params: {
        rawMaterialId: string;
        startCreatedDate?: string;
        endCreatedDate?: string;
        logType?: number;
        offset?: number;
        limit?: number;
        sortBy?: string;
        order?: 'ASC' | 'DESC';
      } = {
        rawMaterialId: String(data.id),
        offset: page,
        limit: pageSize,
        sortBy: sortBy,
        order: order,
      };

      if (appliedStartDate) {
        params.startCreatedDate = appliedStartDate.format('YYYY-MM-DD');
      }
      if (appliedEndDate) {
        params.endCreatedDate = appliedEndDate.format('YYYY-MM-DD');
      }
      if (appliedLogType !== 'all') {
        params.logType = Number(appliedLogType);
      }

      const response = await fetchRawMaterialLogs(params);
      setLogs(response.data || []);
      setTotalItems(response.totalItems || 0);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbarRef.current(errorMessage, 'error');
      setLogs([]);
      setTotalItems(0);
    } finally {
      setLogsLoading(false);
    }
  };

  // Fetch logs when tab changes to Logs or when filters/pagination/sorting changes
  useEffect(() => {
    if (tabValue === 1 && data?.id) {
      fetchLogs();
    }
  }, [
    tabValue,
    data?.id,
    appliedStartDate,
    appliedEndDate,
    appliedLogType,
    page,
    pageSize,
    sortBy,
    order,
  ]);

  // Date validation function
  const validateDates = (
    start: Dayjs | null,
    end: Dayjs | null
  ): { startDate: string; endDate: string } => {
    const errors = { startDate: '', endDate: '' };
    const today = dayjs().endOf('day');

    // Validate start date
    if (start) {
      if (start.isAfter(today)) {
        errors.startDate = 'Start date cannot be in the future';
      }
    }

    // Validate end date
    if (end) {
      if (end.isAfter(today)) {
        errors.endDate = 'End date cannot be in the future';
      }
    }

    // Validate date range
    if (start && end && !errors.startDate && !errors.endDate) {
      if (start.isAfter(end)) {
        errors.endDate =
          'End date must be the same as or later than the start date';
      } else {
        const daysDiff = end.diff(start, 'day');
        if (daysDiff > 31) {
          errors.endDate = 'Date range cannot exceed 31 days';
        }
      }
    }

    return errors;
  };

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue);
    const errors = validateDates(newValue, endDate);
    setDateErrors(errors);
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    setEndDate(newValue);
    const errors = validateDates(startDate, newValue);
    setDateErrors(errors);
  };

  const handleLogTypeChange = (event: SelectChangeEvent<unknown>) => {
    setLogType(event.target.value as string);
  };

  const handleApplyFilters = () => {
    // Check if there are validation errors
    const errors = validateDates(startDate, endDate);
    if (errors.startDate || errors.endDate) {
      setDateErrors(errors);
      return;
    }

    setPage(1); // Reset to first page when applying filters
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedLogType(logType);
  };

  const handleClearFilters = () => {
    const defaultStartDate = getFirstDayOfMonth();
    const defaultEndDate = getCurrentDate();
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setLogType('all');
    setPage(1);
    setAppliedStartDate(defaultStartDate);
    setAppliedEndDate(defaultEndDate);
    setAppliedLogType('all');
    setDateErrors({ startDate: '', endDate: '' });
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    const newSortBy = newSortModel[0]?.field || 'createdAt';
    const newOrder = (newSortModel[0]?.sort || 'desc').toUpperCase() as
      | 'ASC'
      | 'DESC';
    setPage(1);
    setSortBy(newSortBy);
    setOrder(newOrder);
  };

  // Check if filters have been modified from defaults
  const isFiltersModified = (): boolean => {
    const defaultStartDate = getFirstDayOfMonth();
    const defaultEndDate = getCurrentDate();

    const startDateChanged =
      !startDate?.isSame(defaultStartDate, 'day') ||
      !appliedStartDate?.isSame(defaultStartDate, 'day');
    const endDateChanged =
      !endDate?.isSame(defaultEndDate, 'day') ||
      !appliedEndDate?.isSame(defaultEndDate, 'day');
    const logTypeChanged = logType !== 'all' || appliedLogType !== 'all';

    return startDateChanged || endDateChanged || logTypeChanged;
  };

  const formatDate = (dateString: string): string => {
    return dayjs(dateString).format('DD/MM/YYYY');
  };

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 0.8,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'logType',
      headerName: 'Log Type',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      renderCell: (params) => (
        <LogTypeBadge logType={params.value}>{params.value}</LogTypeBadge>
      ),
    },
    {
      field: 'beforeQuantity',
      headerName: 'Before Qty',
      flex: 0.8,
      headerAlign: 'right',
      sortable: false,
      align: 'right',
      renderCell: (params) => (
        <RightAlignedCellBox>
          {new Intl.NumberFormat('en-US').format(params.value)}
        </RightAlignedCellBox>
      ),
    },
    {
      field: 'changedQuantity',
      headerName: 'Changed Qty',
      flex: 0.8,
      headerAlign: 'right',
      sortable: false,
      align: 'right',
      renderCell: (params) => (
        <RightAlignedCellBox>
          <QuantityChange isPositive={params.value >= 0}>
            {params.value >= 0 ? '+' : ''}
            {new Intl.NumberFormat('en-US').format(params.value)}
          </QuantityChange>
        </RightAlignedCellBox>
      ),
    },
    {
      field: 'afterQuantity',
      headerName: 'After Qty',
      flex: 0.8,
      headerAlign: 'right',
      align: 'right',
      sortable: false,
      renderCell: (params) => (
        <RightAlignedCellBox>
          {new Intl.NumberFormat('en-US').format(params.value)}
        </RightAlignedCellBox>
      ),
    },
    {
      field: 'logTypeReference',
      headerName: 'Reference',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const displayValue = params.value || 'N/A';
        return (
          <ReferenceCellContainer title={displayValue}>
            {displayValue}
          </ReferenceCellContainer>
        );
      },
    },
  ];

  const rows = logs.map((log) => ({
    id: log.id,
    createdAt: log.createdAt,
    logType: log.logType,
    beforeQuantity: log.beforeQuantity,
    changedQuantity: log.changedQuantity,
    afterQuantity: log.afterQuantity,
    logTypeReference: log.logTypeReference || 'N/A',
  }));

  if (!data) return null;

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <FixedHeaderWrapper>
        <HeaderSection>
          <HeaderLeft>
            <TitleTypography>{data.name}</TitleTypography>
            <Status status={data.isActive ? 'Active' : 'Inactive'} />
          </HeaderLeft>
          <CloseIcon onClick={handleClose}>
            <i className="bx bx-x-circle" />
          </CloseIcon>
        </HeaderSection>

        <StyledTabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="raw material details tabs"
        >
          <StyledTab
            label="Details"
            id="raw-material-tab-0"
            aria-controls="raw-material-tabpanel-0"
            selected={tabValue === 0}
          />
          <StyledTab
            label="Logs"
            id="raw-material-tab-1"
            aria-controls="raw-material-tabpanel-1"
            selected={tabValue === 1}
          />
        </StyledTabs>
      </FixedHeaderWrapper>

      <ContentWrapper>
        <TabPanel value={tabValue} index={0}>
          <DetailsTable>
            <DetailsRow>
              <DetailsCell isLabel>Code</DetailsCell>
              <DetailsCell>{data.code}</DetailsCell>
            </DetailsRow>
            <DetailsRow>
              <DetailsCell isLabel>Name</DetailsCell>
              <DetailsCell>{data.name}</DetailsCell>
            </DetailsRow>
            <DetailsRow>
              <DetailsCell isLabel>Unit</DetailsCell>
              <DetailsCell>{data.unit}</DetailsCell>
            </DetailsRow>
            <DetailsRow>
              <DetailsCell isLabel>Unit Price</DetailsCell>
              <DetailsCell>
                {formatCurrency(Number(data.unitPrice))}
              </DetailsCell>
            </DetailsRow>
            <DetailsRow>
              <DetailsCell isLabel>Available Quantity</DetailsCell>
              <DetailsCell>{data.availableQty}</DetailsCell>
            </DetailsRow>
            <DetailsRow>
              <DetailsCell isLabel>Minimum Quantity Level</DetailsCell>
              <DetailsCell>{data.minLevelQty}</DetailsCell>
            </DetailsRow>
            <DetailsRow isLast>
              <DetailsCell isLabel>Category</DetailsCell>
              <DetailsCell>{data.category || 'Uncategorized'}</DetailsCell>
            </DetailsRow>
          </DetailsTable>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <LogsFilterContainer>
            <FilterField>
              <FilterLabel>Start Date</FilterLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={startDate}
                  onChange={handleStartDateChange}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      placeholder: 'Start Date',
                      width: '150px',
                      height: '30px',
                      fontSize: '12px',
                      error: !!dateErrors.startDate,
                    } as object,
                    desktopPaper: {
                      component: StyledDatePickerPaper,
                    } as object,
                  }}
                  slots={{
                    textField: StyledDatePickerTextField,
                    openPickerIcon: () => (
                      <CalendarIcon className="bx bx-calendar" />
                    ),
                  }}
                />
              </LocalizationProvider>
              {dateErrors.startDate && (
                <DateFilterErrorText>
                  {dateErrors.startDate}
                </DateFilterErrorText>
              )}
            </FilterField>

            <FilterField>
              <FilterLabel>End Date</FilterLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={endDate}
                  onChange={handleEndDateChange}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      placeholder: 'End Date',
                      width: '150px',
                      height: '30px',
                      fontSize: '12px',
                      error: !!dateErrors.endDate,
                    } as object,
                    desktopPaper: {
                      component: StyledDatePickerPaper,
                    } as object,
                  }}
                  slots={{
                    textField: StyledDatePickerTextField,
                    openPickerIcon: () => (
                      <CalendarIcon className="bx bx-calendar" />
                    ),
                  }}
                />
              </LocalizationProvider>
              {dateErrors.endDate && (
                <DateFilterErrorText>{dateErrors.endDate}</DateFilterErrorText>
              )}
            </FilterField>

            <FilterField>
              <FilterLabel>Log Type</FilterLabel>
              <LogTypeSelect
                value={logType}
                onChange={handleLogTypeChange}
                displayEmpty
              >
                <MenuItem value="all">All</MenuItem>
                {Object.entries(RawMaterialLogTypeLabels).map(
                  ([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  )
                )}
              </LogTypeSelect>
            </FilterField>

            <FilterButtonsContainer>
              {isFiltersModified() && (
                <ClearButton onClick={handleClearFilters}>Clear</ClearButton>
              )}
              <ApplyButton
                onClick={handleApplyFilters}
                disabled={!!dateErrors.startDate || !!dateErrors.endDate}
              >
                Apply
              </ApplyButton>
            </FilterButtonsContainer>
          </LogsFilterContainer>

          {logsLoading ? (
            <LoadingTable columns={columns} />
          ) : (
            <StyledGridContainer
              headerHeight="307px"
              pagination={
                totalItems > 0 && (
                  <PaginationControl
                    totalItems={totalItems}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    loading={logsLoading}
                  />
                )
              }
            >
              <StyledDataGrid
                rows={rows}
                columns={columns}
                disableColumnMenu
                sortModel={sortModel}
                sortingMode="server"
                sortingOrder={['asc', 'desc']}
                onSortModelChange={handleSortModelChange}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                }}
              />
            </StyledGridContainer>
          )}
        </TabPanel>
      </ContentWrapper>
    </StyledDialog>
  );
};

export default ViewRawMaterialDialog;
